import { HttpEvent, HttpEventType } from "@angular/common/http";
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { Observable, Subscription } from "rxjs";
import { VideoEvent } from "../video-player/video-player.component";
import { VideoProcessTO } from "./model/video-process.model";
import { VideoProcessService } from "./services/video-process.service";

@Component({
    selector: "video-in-process",
    templateUrl: "./video-in-process.component.html",
    styleUrls: ["./video-in-process.component.scss"]
})
export class VideoInProcessComponent implements OnInit, OnDestroy, OnChanges {
    public videoProcess: VideoProcessTO = null;
    private static regexGuid = /^[0-9A-Fa-f\-]{36}$/g;

    public uploading = false;
    public uploadPercentage = 0;
    public uploadErro = false;

    public videoProcessing = () =>
        this.uploading == false &&
        this.uploadErro == false &&
        this.videoProcess != null &&
        this.videoProcess?.errorCode == null &&
        this.videoProcess?.concluido == false;

    public videoProcessErro = () =>
        this.uploading == false &&
        this.uploadErro == false &&
        this.videoProcess != null &&
        this.videoProcess.errorCode != null &&
        this.videoProcess?.concluido == true;

    public inConversionQueue = () =>
        this.uploading == false &&
        this.uploadErro == false &&
        this.jobId != null &&
        this.videoProcessing() == false &&
        this.videoProcessErro() == false &&
        this.videoProcess?.concluido != true;

    public showVideoPlayer = () =>
        !this.uploading &&
        !this.uploadErro &&
        !this.videoProcessing() &&
        !this.videoProcessErro() &&
        !this.inConversionQueue() &&
        ((this.src != null && this.src.length > 0) || this.externalMediaUrl?.length > 0);

    @Input() blockAdvanceVideo: boolean;

    @Input("interval")
    private interval = 5 * 1000; // 5 s

    @Input("job-id")
    jobId: string;

    @Input("upload-observable")
    public uploadObservable: Observable<HttpEvent<any>>;

    @Output("video-complete")
    private emitVideoComplete = new EventEmitter<boolean>();

    @Output("upload-complete")
    private emitUploadComplete = new EventEmitter<boolean>();

    @Input("src")
    public src: string[] = [];

    @Input("thumbnail")
    public thumbnail: string = null;

    @Input("externalMediaUrl")
    public externalMediaUrl?: string;

    @Input("InitialTimeInSec") initialTimeInSec: number;
    @Output("VideoEvent") videoEventEmitter = new EventEmitter<VideoEvent>();
    @Output("videoProcessError") videoProcessErroEmitter = new EventEmitter<boolean>();
    @Output("videoProcessing") videoProcessingEmitter = new EventEmitter<boolean>();
    @Output("inConversionQueue") inConversionQueueEmitter = new EventEmitter<boolean>();

    public gif: string;
    private timeInterval: any;
    private currentUploadSubscription: Subscription | null = null;

    private uploadSampleLoadedBitsStart = 0;
    private uploadSampleLoadedBitsEnd = 0;
    private uploadSampleLoadedTime = null;
    private uploadSampleTimer;
    public uploadSpeed = 0;

    private static emitVideoCompleteList: string[] = [];

    private gifs: string[] = [
        "assets/images/gurumatch-video-processing-f.gif",
        "assets/images/gurumatch-video-processing-m.gif"
    ];

    constructor(private videoProcessService: VideoProcessService) {
        this.gif = this.gifs[Math.floor(Math.random() * this.gifs.length)];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.jobId && changes.jobId.currentValue != changes.jobId.previousValue) {
            this.changesJobId();
        }

        if (
            changes.uploadObservable &&
            changes.uploadObservable.currentValue != changes.uploadObservable.previousValue
        ) {
            this.processSubscriptionUpload(changes.uploadObservable.currentValue as Observable<HttpEvent<any>>);
        }
    }

    ngOnDestroy(): void {
        if (this.timeInterval) clearInterval(this.timeInterval);
        
        // Clean up upload subscription to prevent memory leaks
        if (this.currentUploadSubscription) {
            console.log('[VIDEO-IN-PROCESS] Cleaning up upload subscription on component destroy');
            this.currentUploadSubscription.unsubscribe();
            this.currentUploadSubscription = null;
        }
    }

    ngOnInit() {
        this.processSubscription();
    }

    processSubscription(): void {
        this.videoProcessService.videoProcessAsync().subscribe((videoProcess) => {
            this.videoProcess = videoProcess[this.jobId];
            this.videoProcessErroEmitter.emit(this.videoProcessErro());
            this.videoProcessingEmitter.emit(this.videoProcessing());
            this.inConversionQueueEmitter.emit(this.inConversionQueue());

            if (
                this.videoProcess &&
                this.videoProcess.concluido &&
                (this.videoProcess.save || !this.videoProcess.errorMessage)
            ) {
                if (VideoInProcessComponent.emitVideoCompleteList.find((a) => a == this.jobId) != null) {
                    if (this.timeInterval) {
                        clearInterval(this.timeInterval);
                        this.timeInterval = null;
                    }

                    // dispara evento duplicado
                    console.log("Evento discartado " + this.jobId);
                    return;
                }

                VideoInProcessComponent.emitVideoCompleteList.push(this.jobId);
                // Aguarda MongoDB Efetivar Mudança
                setTimeout(() => {
                    this.emitVideoComplete.emit(true);
                }, 2000);

                if (this.timeInterval) {
                    clearInterval(this.timeInterval);
                    this.timeInterval = null;
                }
            }
        });
    }

    processSubscriptionUpload(upload: Observable<HttpEvent<any>>) {        
        console.log('[VIDEO-IN-PROCESS] processSubscriptionUpload started', {
            uploadObservable: upload != null,
            currentUploadPercentage: this.uploadPercentage,
            uploading: this.uploading,
            hasCurrentSubscription: this.currentUploadSubscription != null,
            timestamp: new Date().toISOString()
        });

        // Clean up previous upload subscription to prevent memory leaks and state conflicts
        if (this.currentUploadSubscription) {
            console.log('[VIDEO-IN-PROCESS] Cleaning up previous upload subscription');
            this.currentUploadSubscription.unsubscribe();
            this.currentUploadSubscription = null;
        }

        // Validate environment before starting upload
        if (!this.validateUploadEnvironment()) {
            console.error('[VIDEO-IN-PROCESS] Upload environment validation failed');
            this.uploading = false;
            this.uploadPercentage = 0;
            this.uploadErro = true;
            return;
        }

        // Detect network issues that might affect upload
        this.detectNetworkIssues();

        if (upload == null) {
            console.warn('[VIDEO-IN-PROCESS] Upload observable is null, resetting upload state');
            this.uploading = false;
            this.uploadPercentage = 0;
            this.uploadErro = false;
            return;
        }

        // Reset upload state
        this.uploadPercentage = 0;
        this.uploadErro = false;
        console.log('[VIDEO-IN-PROCESS] Upload percentage reset to 0, subscribing to upload observable');

        // Add timeout and retry mechanism for server environments
        this.currentUploadSubscription = upload.subscribe(
            (resp) => {
                console.log('[VIDEO-IN-PROCESS] HTTP Event received', {
                    eventType: resp?.type,
                    eventTypeName: this.getHttpEventTypeName(resp?.type),
                    hasResponse: resp != null,
                    uploading: this.uploading,
                    currentPercentage: this.uploadPercentage,
                    timestamp: new Date().toISOString()
                });

                if (resp == null) {
                    console.warn('[VIDEO-IN-PROCESS] Received null response, resetting upload state');
                    this.uploading = false;
                    this.uploadErro = false;
                    return;
                }

                if (!this.uploading) {
                    console.log('[VIDEO-IN-PROCESS] Starting upload process');
                    this.StartUpload();
                }

                if (resp.type === HttpEventType.Response) {
                    const status = (resp as any).status;
                    console.log('[VIDEO-IN-PROCESS] Upload Response received', {
                        status: status,
                        uploading: this.uploading,
                        previousPercentage: this.uploadPercentage,
                        timestamp: new Date().toISOString()
                    });

                    this.uploadPercentage = 100;
                    console.log('[VIDEO-IN-PROCESS] Upload percentage set to 100%');

                    if (this.uploading) {
                        console.log('[VIDEO-IN-PROCESS] Waiting 2s before emitting upload complete');
                        // Aguarda Mongo Efetivar Upload
                        setTimeout(() => {
                            this.emitUploadComplete.emit(true);
                        }, 2000);
                    }

                    if (status >= 400) {
                        console.error('[VIDEO-IN-PROCESS] Upload failed with status', status);
                        this.uploadErro = true;
                    }

                    this.EndUpload();
                    return;
                }

                if (resp.type == HttpEventType.UploadProgress) {
                    console.log('[VIDEO-IN-PROCESS] Upload Progress Event received');
                    
                    if (this.jobId) {
                        console.log('[VIDEO-IN-PROCESS] Clearing jobId during upload progress');
                        this.jobId = null;
                    }

                    const progressEvent = resp as any;
                    const loaded = progressEvent.loaded;
                    const total = progressEvent.total;

                    console.log('[VIDEO-IN-PROCESS] Progress data received', {
                        loaded: loaded,
                        total: total,
                        loadedType: typeof loaded,
                        totalType: typeof total,
                        loadedIsNumber: typeof loaded === 'number',
                        totalIsNumber: typeof total === 'number',
                        loadedIsValid: loaded != null && !isNaN(loaded) && loaded >= 0,
                        totalIsValid: total != null && !isNaN(total) && total > 0,
                        progressEvent: progressEvent,
                        timestamp: new Date().toISOString()
                    });

                    // Enhanced validation for server environments
                    if (loaded == null || isNaN(loaded) || loaded < 0) {
                        console.warn('[VIDEO-IN-PROCESS] Invalid loaded value', { 
                            loaded, 
                            type: typeof loaded,
                            isNaN: isNaN(loaded),
                            timestamp: new Date().toISOString()
                        });
                        return;
                    }

                    if (total == null || isNaN(total) || total <= 0) {
                        console.warn('[VIDEO-IN-PROCESS] Invalid total value', { 
                            total, 
                            type: typeof total,
                            isNaN: isNaN(total),
                            timestamp: new Date().toISOString()
                        });
                        return;
                    }

                    // Additional validation for server environments
                    if (loaded > total) {
                        console.warn('[VIDEO-IN-PROCESS] Loaded bytes exceed total bytes', {
                            loaded: loaded,
                            total: total,
                            timestamp: new Date().toISOString()
                        });
                        // Don't return, just log the warning and continue with calculation
                    }

                    this.uploadSampleLoadedBitsEnd = loaded;

                    const rawPercentage = (loaded / total) * 100;
                    let newPercentage = Math.round(rawPercentage);

                    console.log('[VIDEO-IN-PROCESS] Calculating progress percentage', {
                        loaded: loaded,
                        total: total,
                        rawPercentage: rawPercentage,
                        roundedPercentage: newPercentage,
                        previousPercentage: this.uploadPercentage,
                        timestamp: new Date().toISOString()
                    });

                    // Ensure percentage is within valid range and handle edge cases
                    const finalPercentage = Math.max(0, Math.min(99, newPercentage)); // Cap at 99% until Response event
                    
                    // Prevent percentage from going backwards (common in server environments)
                    const actualPercentage = Math.max(this.uploadPercentage, finalPercentage);
                    
                    console.log('[VIDEO-IN-PROCESS] Setting upload percentage', {
                        calculatedPercentage: newPercentage,
                        finalPercentage: finalPercentage,
                        actualPercentage: actualPercentage,
                        previousPercentage: this.uploadPercentage,
                        changed: this.uploadPercentage !== actualPercentage,
                        timestamp: new Date().toISOString()
                    });

                    this.uploadPercentage = actualPercentage;
                    
                    // Force change detection for server environments
                    if (typeof (this as any).cdr !== 'undefined') {
                        (this as any).cdr.detectChanges();
                    }

                    // Enhanced logging for debugging server issues
                    if (this.uploadPercentage === 0 && loaded > 0) {
                        console.warn('[VIDEO-IN-PROCESS] Upload percentage is 0% despite having loaded data', {
                            loaded: loaded,
                            total: total,
                            percentage: actualPercentage,
                            rawPercentage: rawPercentage,
                            finalPercentage: finalPercentage,
                            timestamp: new Date().toISOString()
                        });
                    }

                    // Log progress milestones for better debugging
                    if (this.uploadPercentage % 10 === 0 || this.uploadPercentage > 90) {
                        console.log('[VIDEO-IN-PROCESS] Progress milestone reached', {
                            percentage: this.uploadPercentage,
                            loaded: loaded,
                            total: total,
                            timestamp: new Date().toISOString()
                        });
                    }

                    return;
                } else {
                    console.log('[VIDEO-IN-PROCESS] Other HTTP Event type received', {
                        eventType: resp.type,
                        eventTypeName: this.getHttpEventTypeName(resp.type),
                        timestamp: new Date().toISOString()
                    });
                }
            },
            (error) => {
                console.error('[VIDEO-IN-PROCESS] Upload subscription error', {
                    error: error,
                    errorMessage: error?.message,
                    errorStack: error?.stack,
                    uploading: this.uploading,
                    uploadPercentage: this.uploadPercentage,
                    timestamp: new Date().toISOString()
                });

                if (this.uploading) {
                    this.uploadErro = true;
                    this.emitUploadComplete.emit(true);
                    this.EndUpload();
                }
            }
        );

        // Add timeout mechanism and fallback progress for server environments
        let lastProgressTime = Date.now();
        let lastLoadedBytes = 0;
        let fallbackProgressStarted = false;
        let fallbackInterval: any = null;
        
        const progressTimeoutCheck = setInterval(() => {
            const currentTime = Date.now();
            const timeSinceLastProgress = currentTime - lastProgressTime;
            
            // If no progress for 10 seconds and we're still uploading at 0%
            if (timeSinceLastProgress > 10000 && this.uploading && this.uploadPercentage === 0 && !fallbackProgressStarted) {
                console.warn('[VIDEO-IN-PROCESS] No progress events detected, starting fallback progress simulation', {
                    uploadPercentage: this.uploadPercentage,
                    timeSinceLastProgress: timeSinceLastProgress,
                    timestamp: new Date().toISOString()
                });
                
                fallbackProgressStarted = true;
                this.startFallbackProgress();
            }
            
            // If no progress for 30 seconds and we're still uploading
            if (timeSinceLastProgress > 30000 && this.uploading && this.uploadPercentage < 100) {
                console.warn('[VIDEO-IN-PROCESS] Upload timeout detected - no progress for 30 seconds', {
                    uploadPercentage: this.uploadPercentage,
                    timeSinceLastProgress: timeSinceLastProgress,
                    lastLoadedBytes: lastLoadedBytes,
                    currentLoadedBytes: this.uploadSampleLoadedBitsEnd,
                    timestamp: new Date().toISOString()
                });
            }
            
            // Update progress tracking
            if (this.uploadSampleLoadedBitsEnd > lastLoadedBytes) {
                lastProgressTime = currentTime;
                lastLoadedBytes = this.uploadSampleLoadedBitsEnd;
                
                // Stop fallback if real progress is detected
                if (fallbackProgressStarted && fallbackInterval) {
                    console.log('[VIDEO-IN-PROCESS] Real progress detected, stopping fallback progress');
                    clearInterval(fallbackInterval);
                    fallbackInterval = null;
                    fallbackProgressStarted = false;
                }
            }
            
            // Clean up interval when upload is complete or failed
            if (!this.uploading || this.uploadErro || this.uploadPercentage >= 100) {
                clearInterval(progressTimeoutCheck);
                if (fallbackInterval) {
                    clearInterval(fallbackInterval);
                }
            }
        }, 5000); // Check every 5 seconds
    }

    private StartUpload() {
        if (this.timeInterval) {
            clearInterval(this.timeInterval);
            this.timeInterval = null;
        }

        this.jobId = null;
        this.uploading = true;
        this.uploadErro = false;

        this.uploadSampleLoadedTime = new Date().valueOf();
        this.uploadSampleLoadedBitsStart = 0;
        this.uploadSampleLoadedBitsEnd = 0;

        if (this.uploadSampleTimer) clearInterval(this.uploadSampleTimer);
        this.uploadSampleTimer = setInterval(() => {
            this.CalcUploadSeepd();
        }, 3 * 1000);
    }

    private EndUpload() {
        this.uploading = false;
        this.uploadSampleLoadedBitsStart = 0;
        this.uploadSampleLoadedBitsEnd = 0;
        if (this.uploadSampleTimer) clearInterval(this.uploadSampleTimer);
    }

    private CalcUploadSeepd() {
        // Captura
        const uploadSampleLoadedBits = this.uploadSampleLoadedBitsEnd - this.uploadSampleLoadedBitsStart;
        const time = new Date().valueOf() - this.uploadSampleLoadedTime;

        // Registra
        this.uploadSampleLoadedBitsStart = this.uploadSampleLoadedBitsEnd;
        this.uploadSampleLoadedTime = new Date().valueOf();

        // Calcula
        this.uploadSpeed = uploadSampleLoadedBits / (time / 1000);
    }

    private getHttpEventTypeName(eventType: number): string {
        switch (eventType) {
            case HttpEventType.Sent:
                return "Sent";
            case HttpEventType.UploadProgress:
                return "UploadProgress";
            case HttpEventType.ResponseHeader:
                return "ResponseHeader";
            case HttpEventType.DownloadProgress:
                return "DownloadProgress";
            case HttpEventType.Response:
                return "Response";
            case HttpEventType.User:
                return "User";
            default:
                return `Unknown(${eventType})`;
        }
    }

    private changesJobId() {
        if (this.jobId != null && this.jobId.match(VideoInProcessComponent.regexGuid) == null) {
            // console.log("jobid old", this.jobId);
            this.jobId = null;
            return;
        }

        this.videoProcess = null;

        if (this.timeInterval) clearInterval(this.timeInterval);
        this.videoProcessService.getVideoProcess(this.jobId);
        this.timeInterval = setInterval(() => {
            if (this.jobId) {
                this.videoProcessService.getVideoProcess(this.jobId);
            }
        }, this.interval);
    }

    /**
     * Format bytes as human-readable text.
     *
     * @param bytes Number of bytes.
     * @param si True to use metric (SI) units, aka powers of 1000. False to use
     *           binary (IEC), aka powers of 1024.
     * @param dp Number of decimal places to display.
     *
     * @return Formatted string.
     */
    public humanFileSize(bytes, si = false, dp = 1) {
        const thresh = si ? 1000 : 1024;

        if (Math.abs(bytes) < thresh) {
            return bytes + " B";
        }

        const units = si
            ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
            : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
        let u = -1;
        const r = 10 ** dp;

        do {
            bytes /= thresh;
            ++u;
        } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

        return bytes.toFixed(dp) + " " + units[u];
    }

    private detectNetworkIssues(): void {
        // Monitor network connectivity for server environments
        if (typeof navigator !== 'undefined' && 'connection' in navigator) {
            const connection = (navigator as any).connection;
            if (connection) {
                console.log('[VIDEO-IN-PROCESS] Network connection info', {
                    effectiveType: connection.effectiveType,
                    downlink: connection.downlink,
                    rtt: connection.rtt,
                    saveData: connection.saveData,
                    timestamp: new Date().toISOString()
                });

                // Warn about slow connections
                if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
                    console.warn('[VIDEO-IN-PROCESS] Slow network detected, upload may be unstable', {
                        effectiveType: connection.effectiveType,
                        timestamp: new Date().toISOString()
                    });
                }
            }
        }

        // Monitor online/offline status
        if (typeof navigator !== 'undefined') {
            console.log('[VIDEO-IN-PROCESS] Browser online status', {
                online: navigator.onLine,
                timestamp: new Date().toISOString()
            });

            // Add event listeners for network status changes
            window.addEventListener('online', () => {
                console.log('[VIDEO-IN-PROCESS] Network connection restored', {
                    timestamp: new Date().toISOString()
                });
            });

            window.addEventListener('offline', () => {
                console.warn('[VIDEO-IN-PROCESS] Network connection lost', {
                    uploadPercentage: this.uploadPercentage,
                    uploading: this.uploading,
                    timestamp: new Date().toISOString()
                });
            });
        }
    }

    private validateUploadEnvironment(): boolean {
        console.log('[VIDEO-IN-PROCESS] Validating upload environment', {
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
            online: typeof navigator !== 'undefined' ? navigator.onLine : 'unknown',
            timestamp: new Date().toISOString()
        });

        // Check if we're in a browser environment
        if (typeof window === 'undefined' || typeof navigator === 'undefined') {
            console.warn('[VIDEO-IN-PROCESS] Not running in browser environment');
            return false;
        }

        // Check network connectivity
        if (!navigator.onLine) {
            console.warn('[VIDEO-IN-PROCESS] No network connectivity detected');
            return false;
        }

        return true;
    }

    private startFallbackProgress(): void {
        console.log('[VIDEO-IN-PROCESS] Starting fallback progress simulation for server environment');
        
        let fallbackPercentage = 1; // Start at 1% to show progress
        
        const fallbackInterval = setInterval(() => {
            if (!this.uploading || this.uploadErro || this.uploadPercentage >= 90) {
                console.log('[VIDEO-IN-PROCESS] Stopping fallback progress - upload state changed');
                clearInterval(fallbackInterval);
                return;
            }
            
            // Simulate gradual progress, but cap at 90% to wait for real completion
            if (fallbackPercentage < 90) {
                // Slower progress simulation for larger files
                const increment = Math.random() * 2 + 0.5; // Random increment between 0.5% and 2.5%
                fallbackPercentage = Math.min(90, fallbackPercentage + increment);
                
                // Only update if fallback percentage is higher than current
                if (fallbackPercentage > this.uploadPercentage) {
                    const previousPercentage = this.uploadPercentage;
                    this.uploadPercentage = Math.round(fallbackPercentage);
                    
                    console.log('[VIDEO-IN-PROCESS] Fallback progress update', {
                        previousPercentage: previousPercentage,
                        newPercentage: this.uploadPercentage,
                        fallbackPercentage: fallbackPercentage,
                        timestamp: new Date().toISOString()
                    });
                    
                    // Force change detection
                    if (typeof (this as any).cdr !== 'undefined') {
                        (this as any).cdr.detectChanges();
                    }
                }
            }
        }, 2000); // Update every 2 seconds
    }
}
